import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Task, TaskDetails } from '@models/task/task.model';
import { User } from '@models/user/user.model';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';

@Injectable()
export class AIService {
  baseHref = `${environment.apiUrl}/ai`;
  userSettingsHref = `${environment.apiUrl}/users-settings`;

  constructor(private http: HttpClient) {}

  getElaboratingQuestionsPromptResult(task: Task): Observable<any> {
    const url = `${this.baseHref}/get-elaborating-questions-for-task-desc`;
    return this.http.post<any>(url, { ...task });
  }

  storingUserInputForPromptResult(
    promptResultId: number,
    userInput: string,
  ): Observable<any> {
    const url = `${this.baseHref}/store-user-input-for-prompt-result`;
    return this.http.post<any>(url, { promptResultId, userInput });
  }

  generateOfferMessage(
    task: TaskDetails,
    user: User,
    instructions?: string,
  ): Observable<{ message: string }> {
    const url = `${this.baseHref}/generate-offer-message`;

    // Prepare task data with null checks
    const taskData = {
      taskDescription: task?.description || '',
      taskTitle: task?.title || '',
      taskBudget: task?.budget || 0,
      taskBudgetType: task?.budgetType || 'total',
      taskLocation: task?.city || '',
      taskIsRemote: task?.isRemote || false,
      bringTools: task?.toolsNeeded || false,
      taskOwnerName: task?.taskOwnerName || '',
      isTaskFlexible: task?.isTaskFlexible || false,
      anyTimeActive: task?.anyTimeActive || false,
      elaboratingQuestionsAnswers: task?.elaboratingQuestionsAnswers || [],
      dueDate: task?.dueDate,
      morning: task?.morning || false,
      midday: task?.midday || false,
      afternoon: task?.afternoon || false,
      evening: task?.evening || false,
    };

    // Prepare user data with null checks and ensure ID is present
    const userData = {
      id: user?.id, // Ensure ID is passed directly
      userId: user?.id, // Add userId field as well for backend compatibility
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      completionRate: user?.completionRate || 0,
      rating: user?.rating || 0,
      reviewCount: user?.reviewCount || 0,
      completedTasks: user?.completedTasks || 0,
      offersMade: user?.offersMade || 0,
      description: user?.description || '',
      city: user?.city || '',
      bankVerified: user?.bankVerified || false,
      phoneVerified: user?.phoneVerified || false,
      stripe_verified: user?.stripe_verified || false,
      companyVerified: user?.companyVerified || false,
      electricityVerified: user?.electricityVerified || false,
      sewageVerified: user?.sewageVerified || false,
      plumbingVerified: user?.plumbingVerified || false,
    };
    return this.http.post<{ message: string }>(url, {
      task: taskData,
      user: userData,
      instructions: instructions || '',
    });
  }

  saveOfferMessageInstructions(
    instructions: string,
  ): Observable<{ success: boolean; instructions: string }> {
    if (!instructions) {
      throw new Error('Missing instructions');
    }
    const url = `${this.baseHref}/save-offer-message-instructions`;
    return this.http.post<{ success: boolean; instructions: string }>(url, {
      instructions,
    });
  }

  getOfferMessageInstructions(): Observable<{ instructions: string | null }> {
    const url = `${this.baseHref}/get-offer-message-instructions`;
    return this.http.get<{ instructions: string | null }>(url);
  }
}
